import { RecurrenceFrequency, Task } from "../types";
import { nextOccurrence } from "../../supabase/functions/_shared/recurrence";

export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval: number;
  dayOfWeek: number | null; // 0=Sunday..6=Saturday
  endDate: string | null; // YYYY-MM-DD
}

/**
 * Convert a Task's recurrence columns to a RecurrenceRule.
 * Returns null if the task has no recurrence frequency.
 */
export function ruleFromTask(
  task: Pick<
    Task,
    | "recurrence_frequency"
    | "recurrence_interval"
    | "recurrence_day_of_week"
    | "recurrence_end_date"
  >,
): RecurrenceRule | null {
  if (!task.recurrence_frequency) return null;
  return {
    frequency: task.recurrence_frequency,
    interval: task.recurrence_interval,
    dayOfWeek: task.recurrence_day_of_week,
    endDate: task.recurrence_end_date,
  };
}

/** Field values that represent "no recurrence". Apply these to clear a task's recurrence. */
export const CLEARED_RECURRENCE = {
  recurrence_frequency: null,
  recurrence_interval: 1,
  recurrence_day_of_week: null,
  recurrence_end_date: null,
} as const satisfies Pick<
  Task,
  "recurrence_frequency" | "recurrence_interval" | "recurrence_day_of_week" | "recurrence_end_date"
>;

/** Today's calendar date where this machine is sitting, as YYYY-MM-DD. */
function todayLocal(): string {
  return formatDate(new Date());
}

/**
 * Given a current scheduled_date and a recurrence rule,
 * compute the next occurrence date (YYYY-MM-DD).
 * Returns null if the next date would be past the endDate.
 */
export function getNextOccurrenceDate(
  currentDate: string,
  rule: RecurrenceRule,
): string | null {
  return nextOccurrence(currentDate, rule, todayLocal());
}

/**
 * Compute the first occurrence date (YYYY-MM-DD) implied by `rule`, anchored
 * to today. Today wins if today matches the rule (e.g. weekly with
 * dayOfWeek = today.getDay()); otherwise we move forward to the next match.
 *
 * Used when toggling recurrence on or changing the rule, since recurring tasks
 * derive their scheduled_date from the rule rather than from a manually-picked
 * date.
 */
export function getFirstOccurrenceFromToday(rule: RecurrenceRule): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (rule.frequency === "weekly") {
    const targetDay = rule.dayOfWeek ?? today.getDay();
    const daysAhead = (targetDay - today.getDay() + 7) % 7;
    const result = new Date(today);
    result.setDate(today.getDate() + daysAhead);
    return formatDate(result);
  }

  // For daily/monthly/yearly the rule re-anchors at today's day-of-month /
  // month-day, so today is always the first occurrence.
  return formatDate(today);
}

/**
 * Compute the next date (YYYY-MM-DD) on which `dayOfMonth` falls, from today.
 * Today wins if today's day-of-month equals dayOfMonth. Otherwise we walk
 * forward month by month until one both contains the day and lands on or after
 * today.
 *
 * Months too short to hold the day are skipped rather than clamped. The date
 * returned here becomes the rule's stored anchor, and `getNextOccurrenceDate`
 * reads the day-of-month back off that anchor on every later occurrence — so a
 * clamp here wouldn't move one occurrence, it would silently redefine "the
 * 31st" as "the 30th" for the life of the task. Short months are still handled,
 * by the per-occurrence clamping there (31 → Feb 28).
 */
export function getNextDayOfMonthFromToday(dayOfMonth: number): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const day = Math.min(Math.max(1, Math.trunc(dayOfMonth)), 31);

  // A 12-month window always contains a 31-day month, so this resolves for
  // every day 1–31 well before the bound.
  for (let offset = 0; offset <= 12; offset++) {
    const month = today.getMonth() + offset;
    const lastDay = new Date(today.getFullYear(), month + 1, 0).getDate();
    if (day > lastDay) continue;
    const candidate = new Date(today.getFullYear(), month, day);
    if (candidate >= today) return formatDate(candidate);
  }

  return formatDate(clampedDateInMonth(today.getFullYear(), today.getMonth() + 1, day));
}

/**
 * Compute the next date (YYYY-MM-DD) on which the given month+day falls, from
 * today. Today wins if it matches; otherwise we try this year, then next year.
 * Day is clamped to the month's length (Feb 30 → Feb 28/29).
 */
export function getNextMonthDayFromToday(monthZeroBased: number, dayOfMonth: number): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const thisYear = clampedDateInMonth(today.getFullYear(), monthZeroBased, dayOfMonth);
  if (thisYear >= today) return formatDate(thisYear);

  const nextYear = clampedDateInMonth(today.getFullYear() + 1, monthZeroBased, dayOfMonth);
  return formatDate(nextYear);
}

/** Build a Date for `year/month/day`, clamping day to the month's max length. */
function clampedDateInMonth(year: number, monthZeroBased: number, day: number): Date {
  const lastDay = new Date(year, monthZeroBased + 1, 0).getDate();
  return new Date(year, monthZeroBased, Math.min(Math.max(1, day), lastDay));
}

/**
 * Count the recurrence occurrences from `currentDate` (inclusive) up to but not
 * including today. Returns 0 if `currentDate` is today or in the future.
 *
 * The result tells the user "how many fire days have I let pass without
 * completing this task" — used to decide whether to surface a catch-up button.
 */
export function countMissedOccurrences(
  currentDate: string,
  rule: RecurrenceRule,
): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = parseLocalDate(currentDate);
  if (start >= today) return 0;

  const origDay = start.getDate();
  const origMonth = start.getMonth();

  let count = 0;
  let cursor = new Date(start);

  while (cursor < today) {
    count++;
    switch (rule.frequency) {
      case "daily":
        cursor.setDate(cursor.getDate() + rule.interval);
        break;
      case "weekly":
        cursor.setDate(cursor.getDate() + rule.interval * 7);
        break;
      case "monthly":
        cursor = addMonths(cursor, rule.interval, origDay);
        break;
      case "yearly":
        cursor.setFullYear(cursor.getFullYear() + rule.interval);
        clampToMonth(cursor, origMonth, origDay);
        break;
    }
  }

  return count;
}

const BYDAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

/**
 * Build an RFC-5545 RRULE string for a Google Calendar recurring event.
 *
 * Examples:
 *   { frequency: "daily", interval: 1 }                                  -> "RRULE:FREQ=DAILY"
 *   { frequency: "daily", interval: 2 }                                  -> "RRULE:FREQ=DAILY;INTERVAL=2"
 *   { frequency: "weekly", interval: 1, dayOfWeek: 5 }                   -> "RRULE:FREQ=WEEKLY;BYDAY=FR"
 *   { frequency: "weekly", interval: 2, dayOfWeek: 0 }                   -> "RRULE:FREQ=WEEKLY;INTERVAL=2;BYDAY=SU"
 *   { frequency: "monthly", interval: 1 }                                -> "RRULE:FREQ=MONTHLY"
 *   { frequency: "yearly", interval: 1, endDate: "2026-08-15" }          -> "RRULE:FREQ=YEARLY;UNTIL=20260815T235959Z"
 */
export function buildRRule(rule: RecurrenceRule): string {
  const parts: string[] = [`FREQ=${rule.frequency.toUpperCase()}`];

  if (rule.interval > 1) {
    parts.push(`INTERVAL=${rule.interval}`);
  }

  if (rule.frequency === "weekly" && rule.dayOfWeek != null) {
    parts.push(`BYDAY=${BYDAY_CODES[rule.dayOfWeek]}`);
  }

  if (rule.endDate) {
    const compact = rule.endDate.replace(/-/g, "");
    parts.push(`UNTIL=${compact}T235959Z`);
  }

  return `RRULE:${parts.join(";")}`;
}

/**
 * Format a date occurrence as an EXDATE entry for a Google Calendar event's
 * `recurrence` array. Used to suppress a single instance of a recurring event.
 *
 * For all-day events (no time), produces `EXDATE;VALUE=DATE:YYYYMMDD`.
 * For timed events, produces `EXDATE;TZID=<timezone>:YYYYMMDDTHHMMSS`.
 */
export function buildExdate(
  occurrenceDate: string,
  scheduledTime: string | null,
  timezone: string,
): string {
  const [y, m, d] = occurrenceDate.split("-");
  if (!scheduledTime) {
    return `EXDATE;VALUE=DATE:${y}${m}${d}`;
  }
  const [hh, mm] = scheduledTime.split(":");
  const ss = "00";
  return `EXDATE;TZID=${timezone}:${y}${m}${d}T${hh}${mm}${ss}`;
}

/** Add N months to a date, clamping to the last day of the target month if needed. */
function addMonths(date: Date, months: number, originalDay: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  // Clamp: if originalDay was 31 but target month has 28 days, use 28
  const targetMonth = (date.getMonth() + months) % 12;
  clampToMonth(result, targetMonth, originalDay);
  return result;
}

/** Ensure the date's day doesn't overflow the target month. */
function clampToMonth(date: Date, targetMonth: number, originalDay: number): void {
  // Set to the desired day, but cap at month's max
  const maxDay = new Date(date.getFullYear(), targetMonth + 1, 0).getDate();
  const day = Math.min(originalDay, maxDay);
  date.setMonth(targetMonth, day);
}

/** Parse a YYYY-MM-DD string as a local date (not UTC). */
function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Format a Date as YYYY-MM-DD. */
function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** "1st", "2nd", "3rd", "4th"... */
function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

/** Human-readable label for a task's recurrence rule. */
export function getRecurrenceLabel(task: Pick<Task, "recurrence_frequency" | "recurrence_interval" | "recurrence_day_of_week" | "scheduled_date">): string {
  const { recurrence_frequency: freq, recurrence_interval: interval, recurrence_day_of_week: dow, scheduled_date } = task;
  if (!freq) return "";

  const dayOfMonth = scheduled_date ? parseInt(scheduled_date.split("-")[2], 10) : null;
  const monthlySuffix = dayOfMonth ? ` — ${ordinal(dayOfMonth)} of the month` : "";

  if (interval === 1) {
    switch (freq) {
      case "daily":
        return "Daily";
      case "weekly":
        return dow != null ? `Every ${DAY_NAMES[dow]}` : "Weekly";
      case "monthly":
        return `Monthly${monthlySuffix}`;
      case "yearly":
        return "Yearly";
    }
  }

  switch (freq) {
    case "daily":
      return `Every ${interval} days`;
    case "weekly":
      if (dow != null) return `Every ${interval} weeks on ${DAY_NAMES[dow]}`;
      return `Every ${interval} weeks`;
    case "monthly":
      return `Every ${interval} months${monthlySuffix}`;
    case "yearly":
      return `Every ${interval} years`;
  }
}
